<template>
  <Dialog
    v-model:visible="isOpen"
    modal
    header="A new interface has arrived!"
    :style="{ width: 'min(440px, 95vw)' }"
    :dismissable-mask="true"
    class="interface-unlock-dialog"
    @hide="onHide"
  >
    <div class="unlock-body">
      <div class="unlock-icon">
        <i class="pi pi-star" />
      </div>
      <p class="unlock-text">
        Your judging has earned you <strong>{{ thresholdLabel }} lifetime Points</strong> —
        you have unlocked the <strong>Trivium Lite</strong> interface.
      </p>
      <p class="unlock-hint">
        Find it any time under <strong>Profile → Trivium Lite</strong>.
      </p>
    </div>
    <template #footer>
      <Button label="Got it" @click="close" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Dialog, Button } from '@/components/primevue';
import { useInterfaceUnlock } from '@/composables/useInterfaceUnlock';

const props = defineProps<{ visible: boolean }>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();

const { threshold, markAnnouncementSeen } = useInterfaceUnlock();

const isOpen = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
});

const thresholdLabel = computed(() => threshold.value ?? 18);

function close() {
  isOpen.value = false;
}

function onHide() {
  // Persist server-side: the announcement appears exactly once, ever.
  markAnnouncementSeen();
}
</script>

<style scoped lang="scss">
.unlock-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 8px 4px 0;
}

.unlock-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  color: var(--accent-primary);
  margin-bottom: 16px;

  i {
    font-size: 24px;
  }
}

.unlock-text {
  font-size: 15px;
  color: var(--text-primary);
  line-height: 1.5;
  margin: 0 0 8px 0;
}

.unlock-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 8px 0;
}

:deep(.p-dialog) {
  background: var(--bg-primary);
  color: var(--text-primary);
}

:deep(.p-dialog-header) {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
}

:deep(.p-dialog-content) {
  background: var(--bg-primary);
}

:deep(.p-dialog-footer) {
  background: var(--bg-primary);
  border-top: 1px solid var(--border-primary);
}
</style>
